if (!customElements.get("sticky-header")) {
  customElements.define(
    "sticky-header",
    class StickyHeader extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.stickyDisabled = this.getAttribute("disabled") === "true";
        this.header = document.querySelector(".section-header");
        this.alwaysSticky = this.dataset.alwaysOn === "true";
        this.headerBounds = {};
        this.currentScrollTop = 0;
        this.preventReveal = false;
        this.predictiveSearch = this.querySelector("predictive-search");

        if (this.alwaysSticky) {
          this.header.classList.add("section-header-sticky-always");
        }

        this.onScrollHandler = this.onScroll.bind(this);
        this.onResizeHandler = this.measureHeader.bind(this);
        this.hideHeaderOnScrollUp = () => {
          this.preventReveal = true;
        };

        this.addEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.addEventListener("scroll", this.onScrollHandler, false);
        window.addEventListener("resize", this.onResizeHandler, false);

        this.measureHeader();
        this.createObserver();
      }

      disconnectedCallback() {
        this.removeEventListener("preventHeaderReveal", this.hideHeaderOnScrollUp);
        window.removeEventListener("scroll", this.onScrollHandler);
        window.removeEventListener("resize", this.onResizeHandler);
        document.documentElement.style.removeProperty("--sticky-header-offset");
      }

      // Caches the header's height and republishes the offset. Called on connect
      // and on resize so no layout read happens during scroll.
      measureHeader() {
        this.headerHeight = this.header.offsetHeight;
        this.publishOffset();
      }

      // Publishes how much space the header currently occupies at the top of the
      // viewport as --sticky-header-offset, so other pinned elements (the mobile
      // filter and sort bar) can sit directly beneath it in either sticky mode.
      publishOffset() {
        let offset = 0;

        if (this.stickyDisabled) {
          offset = 0;
        } else if (this.alwaysSticky) {
          offset = this.headerHeight;
        } else if (
          this.header.classList.contains("section-header-sticky") &&
          !this.header.classList.contains("section-header-hidden")
        ) {
          offset = this.headerHeight;
        }

        document.documentElement.style.setProperty("--sticky-header-offset", offset + "px");
      }

      createObserver() {
        let observer = new IntersectionObserver((entries, observer) => {
          this.headerBounds = entries[0].intersectionRect;
          observer.disconnect();
        });

        observer.observe(this.header);
      }

      onScroll() {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

        if (this.predictiveSearch && this.predictiveSearch.isOpen) {
          return;
        }

        if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          requestAnimationFrame(this.hide.bind(this));
        } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
          if (!this.preventReveal && !this.stickyDisabled) {
            requestAnimationFrame(this.reveal.bind(this));
          } else {
            window.clearTimeout(this.isScrolling);

            this.isScrolling = setTimeout(() => {
              this.preventReveal = false;
            }, 66);

            requestAnimationFrame(this.hide.bind(this));
          }
        } else if (!this.stickyDisabled && scrollTop <= this.headerBounds.top) {
          requestAnimationFrame(this.reset.bind(this));
        } else if (this.stickyDisabled && scrollTop <= this.headerBounds.bottom) {
          requestAnimationFrame(this.reset.bind(this));
        }

        this.currentScrollTop = scrollTop;
      }

      hide() {
        if (this.alwaysSticky || this.stickyDisabled) {
          return;
        }

        this.header.classList.add("section-header-hidden", "section-header-sticky");
        this.publishOffset();

        // Add methods to close dropdowns etc
        this.closeHeaderDropdowns();
      }

      reveal() {
        if (this.alwaysSticky) {
          return;
        }

        this.header.classList.add("section-header-sticky", "animate");
        this.header.classList.remove("section-header-hidden");
        this.publishOffset();
      }

      reset() {
        this.header.classList.remove("section-header-hidden", "section-header-sticky", "animate");
        this.publishOffset();
      }

      closeHeaderDropdowns() {
        const headerLocalizationDropdowns = this.header.querySelectorAll("localization-form");
        const headerDropdowns = this.header.querySelectorAll(".dropdown-modal");

        headerLocalizationDropdowns.forEach((dropdown) => {
          dropdown.hidePanel();
        });
        headerDropdowns.forEach((dropdown) => {
          dropdown.close();
        });
      }
    },
  );
}
